import { orderBy } from 'lodash'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { StringParam, useQueryParams } from 'use-query-params'

import { Button, SearchInput, Table, TableBody, TableProvider, TableSearchAndFilters } from '@percent/lemonade'
import { useSearchFilter } from '@percent/partner-dashboard/common/hooks'
import { usePermissionCheck } from '@percent/partner-dashboard/common/hooks/useAllow/usePermissionCheck'
import { PartnerApiKey } from '@percent/partner-dashboard/context/auth/authContext/AuthContext.types'
import { useServices } from '@percent/partner-dashboard/context/serviceContext/ServiceContext'
import { useQuery } from '@percent/utility'
import { KeyListNoAccess } from './KeyListNoAccess'
import { KeyRow } from './KeyRow'
import { CreateKeySetModal } from './modals/CreateKeySetModal'

export function KeyList() {
  const { t } = useTranslation()
  const { userCan } = usePermissionCheck()
  const [isCreateKeySetModalOpen, setIsCreateKeySetModalOpen] = useState(false)
  const { accountService } = useServices()

  const {
    data: partnerData,
    isPending: isLoadingPartnerData,
    refetch: refetchPartnerData
  } = useQuery(['v1/account/partners'], accountService.getPartner, {})

  const columns = [
    { id: t('table.header.keySetName') },
    { id: t('table.header.type') },
    { id: t('table.header.createdAt') },
    { id: t('table.header.expiresAt') },
    { id: t('table.header.status') },
    { id: t('table.header.token') },
    { id: '', props: { width: '80px' } },
    { id: '' }
  ]

  const [queryParams, setQueryParams] = useQueryParams({
    query: StringParam
  })

  const [apiKeys, setApiKeys] = useState<PartnerApiKey[]>(partnerData?.apiKeys ?? [])

  const handleOrderApiKeys = useCallback((keys: PartnerApiKey[]) => {
    return orderBy(keys, ['expiresAt', 'setId'], ['desc', 'asc'])
  }, [])

  useEffect(() => {
    if (partnerData?.apiKeys) {
      setApiKeys(handleOrderApiKeys(partnerData.apiKeys))
    }

    const searchQuery = queryParams.query ?? ''

    if (searchQuery) {
      setApiKeys(
        handleOrderApiKeys(
          partnerData?.apiKeys?.filter(key => key.name.toLowerCase().includes(searchQuery.toLowerCase())) ?? []
        )
      )
    }
  }, [partnerData?.apiKeys, handleOrderApiKeys, queryParams.query])

  const { searchValue, handleChange, handleKeyPress, handleClearValue } = useSearchFilter({
    queryParams: { query: queryParams?.query ?? '' },
    setQueryParams: params => setQueryParams?.({ ...queryParams, query: params.query })
  })

  const refetchApiKeys = () => {
    refetchPartnerData()
  }

  if (!userCan('viewApiKeys')) {
    return <KeyListNoAccess />
  }

  return (
    <>
      <TableProvider
        columns={columns}
        totalResults={apiKeys?.length ?? 0}
        isLoading={isLoadingPartnerData}
        illustration="no-results"
        emptyTableTitle={
          queryParams.query ? t('typography.noSearchResults') : t('typography.noListTitle', { listType: 'API keys' })
        }
      >
        <TableSearchAndFilters>
          <SearchInput
            name="search"
            value={searchValue}
            onKeyDown={handleKeyPress}
            onChange={handleChange}
            handleClearValue={handleClearValue}
            placeholder={t('table.keyListSearchPlaceholder')}
          />
          <Button data-testid="create-key-set-btn" onClick={() => setIsCreateKeySetModalOpen(true)}>
            {t('button.createKeySet')}
          </Button>
        </TableSearchAndFilters>
        <Table>
          <TableBody>
            {apiKeys.map(key => (
              <KeyRow key={key.id} apiKey={key} refetchApiKeys={refetchApiKeys} />
            ))}
          </TableBody>
        </Table>
      </TableProvider>
      <CreateKeySetModal
        open={isCreateKeySetModalOpen}
        onClose={() => setIsCreateKeySetModalOpen(false)}
        refetchApiKeys={refetchApiKeys}
      />
    </>
  )
}
